/**
 * /simulador/relatorio/:sessionId — per-session coaching report.
 *
 * Loads:
 *   - The report (`/simulator/report/get`, generated lazily server-side).
 *   - The full transcript via `/simulator/resume` (works for terminal sessions
 *     too — server returns history but does not flip back to active).
 *   - Achievements + heatmap so the user can review progress next to the
 *     specific session feedback.
 *
 * On terminal sessions the server already set status to ended/close_won/
 * close_lost; resume returns the messages array but skips the active-flip
 * branch, so this is safe.
 *
 * Spec: docs/architecture/simulador-spec.md §5.5 / §6.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { ArrowLeft, Spinner } from "@phosphor-icons/react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import { SimuladorReport } from "@/features/simulador/components/SimuladorReport";
import { useSimulatorReport } from "@/features/simulador/hooks/useSimulatorReport";
import { useDeleteSimulatorSession } from "@/features/simulador/hooks/useDeleteSimulatorSession";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { resumeSession, replayFromTurn } from "@/features/simulador/api/client";
import { toast } from "sonner";
import { friendlyError, toastError } from "@/lib/userMessages";
import type { SimulatorMessage } from "@/features/simulador/types";

export default function SimuladorReportPage() {
  usePageTitle("Relatório do Simulador");
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { session } = useSession();

  const reportQ = useSimulatorReport({ sessionId: sessionId ?? null });
  const deleteSession = useDeleteSimulatorSession();

  // Load full transcript via /resume — terminal sessions still return
  // messages; we only need them to render the annotated transcript.
  const [messages, setMessages] = useState<SimulatorMessage[]>([]);
  const [eneatype, setEneatype] = useState<number | undefined>(undefined);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [replaying, setReplaying] = useState(false);

  async function handleReplay(replayFromSeq?: number) {
    if (!sessionId || replaying) return;
    setReplaying(true);
    try {
      const token = await session?.getToken();
      if (!token) {
        toast.error("Sessão expirada. Faça login novamente.");
        return;
      }
      await replayFromTurn(
        { parent_session_id: sessionId, replay_from_seq: replayFromSeq },
        token,
      );
      navigate("/simulador");
    } catch (err) {
      const e = err as Error & { code?: string; status?: number };
      if (e.status === 409 || e.code === "session_already_active") {
        toast.error("Você já tem uma sessão ativa. Encerre antes de repetir.");
      } else {
        toastError(e, "Não foi possível repetir a simulação. Tente novamente.");
      }
    } finally {
      setReplaying(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!sessionId) return;
      try {
        const token = await session?.getToken();
        if (!token) return;
        const res = await resumeSession({ session_id: sessionId, since_seq: 0 }, token);
        if (cancelled) return;
        setMessages(res.messages ?? []);
        setEneatype(res.session.eneatype);
      } catch (err) {
        if (cancelled) return;
        setTranscriptError(friendlyError(err, "Não foi possível carregar a transcrição. Tente atualizar a página."));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, session]);

  if (!sessionId) {
    return (
      <AppLayout>
        <Empty title="Sessão inválida" onBack={() => navigate("/simulador")} />
      </AppLayout>
    );
  }

  const loading = reportQ.isLoading && !reportQ.report;

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-foreground">
            Relatório do <span className="accent-text">Atendimento</span>
          </h1>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/simulador")} className="gap-2">
              <ArrowLeft weight="bold" className="h-4 w-4" />
              Voltar
            </Button>
            {sessionId && (
              <DeleteEntityButton
                entityLabel="simulação"
                confirmDescription="A simulação, mensagens e relatório serão removidos permanentemente."
                onConfirm={async () => {
                  try {
                    await deleteSession.mutateAsync(sessionId);
                    toast.success("Simulação excluída.");
                    navigate("/simulador");
                  } catch (err) {
                    toastError(err, "Não foi possível excluir. Tente novamente.");
                    throw err;
                  }
                }}
                data-testid="simulador-report-delete"
              />
            )}
            <Button variant="outline" onClick={() => navigate("/simulador?new=1")}>
              Nova simulação
            </Button>
          </div>
        </div>

        {loading ? (
          <CenteredSpinner label="Gerando relatório do atendimento…" />
        ) : reportQ.timedOut ? (
          <ErrorBlock
            message="A geração do relatório está demorando mais do que o esperado."
            onRetry={() => void reportQ.refetch()}
          />
        ) : reportQ.error ? (
          <ErrorBlock
            message={reportQ.error}
            onRetry={() => void reportQ.refetch()}
          />
        ) : reportQ.report ? (
          <>
            {transcriptError ? (
              <p className="text-xs text-amber-300">
                Não foi possível carregar a transcrição: {transcriptError}
              </p>
            ) : null}
            <SimuladorReport
              report={reportQ.report}
              transcript={
                (reportQ.transcript && reportQ.transcript.length > 0
                  ? reportQ.transcript
                  : messages
                      .filter((m) => m.role === "user" || m.role === "patient" || m.role === "hint")
                      .map((m) => ({ seq: m.seq, role: m.role as "user" | "patient" | "hint", content: m.content }))
                ) || undefined
              }
              messages={messages}
              eneatype={eneatype}
              onReplayFromStart={() => void handleReplay()}
              onReplayFromSeq={(seq) => void handleReplay(seq)}
              replayDisabled={replaying}
            />
          </>
        ) : (
          <Empty title="Relatório indisponível" onBack={() => navigate("/simulador")} />
        )}
      </div>
    </AppLayout>
  );
}

function CenteredSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
      <Spinner weight="bold" className="w-8 h-8 animate-spin text-primary" aria-hidden />
      <p className="text-sm">{label}</p>
    </div>
  );
}

function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center space-y-3">
      <p className="text-sm text-destructive">{message}</p>
      <Button variant="outline" onClick={onRetry}>
        Tentar de novo
      </Button>
    </div>
  );
}

function Empty({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="max-w-md mx-auto py-16 text-center space-y-4">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <Button onClick={onBack}>Voltar ao Simulador</Button>
    </div>
  );
}
